import React from 'react';

/**
 * Utility / chrome icons (sort chevrons, page-skip arrows, the row-actions
 * ellipsis, the modal close X). Hand-authored inline SVG — all original
 * geometry, no third-party glyph copied (IP-clean).
 *
 * Different aesthetic from `<AppIcon>`: flat, single-colour, inheriting
 * `currentColor`. These are UI affordances meant to read as chrome, not as
 * content — so they intentionally stay uncoloured and skip the polished
 * gradient/glossy treatment.
 *
 * Toolbar "content" icons (notification bell, theme sun/moon toggle) belong
 * in `AppIcon` — see `notification` / `themeSun` / `themeMoon`. The boundary:
 * pure affordances (chevrons, ellipsis, close X, page-skip arrows) stay here;
 * anything the user reads as a recognisable object gets the polished
 * squircle treatment.
 *
 * Usage:
 *   <UiIcon name="chevronDown" className="h-4 w-4" />
 */

// Stroke defaults are applied on the <svg> itself in the renderer below, so a
// shared wrapper element is not needed — each `mode: 'stroke'` body inherits them.
const UI_ICON_DEFS = {
  chevronUp:      { mode: 'stroke', body: <polyline points="6,15 12,9 18,15" /> },
  chevronDown:    { mode: 'stroke', body: <polyline points="6,9 12,15 18,9" /> },
  chevronBack:    { mode: 'stroke', body: <polyline points="15,6 9,12 15,18" /> },
  chevronForward: { mode: 'stroke', body: <polyline points="9,6 15,12 9,18" /> },

  playSkipBack: {
    mode: 'fill',
    body: (
      <g fill="currentColor">
        <polygon points="14,6 14,18 4.5,12" />
        <rect x="16" y="6" width="2" height="12" rx="0.6" />
      </g>
    ),
  },
  playSkipForward: {
    mode: 'fill',
    body: (
      <g fill="currentColor">
        <polygon points="10,6 10,18 19.5,12" />
        <rect x="6" y="6" width="2" height="12" rx="0.6" />
      </g>
    ),
  },

  ellipsisHorizontal: {
    mode: 'fill',
    body: (
      <g fill="currentColor">
        <circle cx="6"  cy="12" r="1.7" />
        <circle cx="12" cy="12" r="1.7" />
        <circle cx="18" cy="12" r="1.7" />
      </g>
    ),
  },

  close: {
    mode: 'stroke',
    body: (
      <g>
        <line x1="6"  y1="6"  x2="18" y2="18" />
        <line x1="18" y1="6"  x2="6"  y2="18" />
      </g>
    ),
  },
  download: {
    mode: 'stroke',
    body: (
      <g>
        <path d="M 12 3 V 15" />
        <path d="M 6.8 10.2 L 12 15.4 L 17.2 10.2" strokeLinejoin="round" />
        <path d="M 4.5 19.5 H 19.5" />
      </g>
    ),
  },

  refresh: {
    mode: 'stroke',
    body: (
      <g>
        <path d="M 20 12 A 8 8 0 1 1 17.2 5.9" />
        <path d="M 17.4 2.6 V 6.4 H 13.6" />
      </g>
    ),
  },

  search: {
    mode: 'stroke',
    body: (
      <g>
        <circle cx="11" cy="11" r="6" />
        <line x1="15.5" y1="15.5" x2="20" y2="20" />
      </g>
    ),
  },

  // Rule 74 v1.68 — bell + bellSlash for the chat MuteToggle. Hand-authored
  // single-stroke glyphs that inherit `currentColor` from the parent button's
  // tinted/muted background. Two icons rather than one toggled class so the
  // active-state visual is unambiguous.
  bell: {
    mode: 'stroke',
    body: (
      <g>
        <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2H4.5L6 16Z" />
        <path d="M10 19a2 2 0 0 0 4 0" />
      </g>
    ),
  },
  bellSlash: {
    mode: 'stroke',
    body: (
      <g>
        <path d="M6 16V11a6 6 0 0 1 .8-3" />
        <path d="M9 8a6 6 0 0 1 9 3v5l1.5 2H8" />
        <path d="M10 19a2 2 0 0 0 4 0" />
        <line x1="4" y1="4" x2="20" y2="20" />
      </g>
    ),
  },

  // Rule 74 v1.76 — mic + micOff for the in-call participant-tile mute
  // indicator. Single-stroke glyphs (currentColor) so the parent can tint
  // them green (active) or red (muted). The slashed variant uses one
  // diagonal stroke across the capsule + stand, no separate hashed icon
  // overlay to keep the geometry simple at 12-16px display sizes.
  mic: {
    mode: 'stroke',
    body: (
      <g>
        <rect x="9" y="3" width="6" height="12" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="8"  y1="22" x2="16" y2="22" />
      </g>
    ),
  },
  micOff: {
    mode: 'stroke',
    body: (
      <g>
        <rect x="9" y="3" width="6" height="12" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="8"  y1="22" x2="16" y2="22" />
        <line x1="3"  y1="3"  x2="21" y2="21" />
      </g>
    ),
  },
  // Requirement met / not yet met, for the password checklist. A tick and a
  // hollow dot rather than a tick and a cross: an unmet rule is "not yet",
  // not an error, and a row of red crosses on a field nobody has typed into
  // reads as failure before the user has done anything wrong.
  check: { mode: 'stroke', body: <polyline points="4,12.5 9.5,18 20,6.5" /> },
  dot: { mode: 'stroke', body: <circle cx="12" cy="12" r="4" /> },
  // Reveal / hide a typed password. Drawn here rather than pulled from an icon
  // set — every glyph in this kit is hand-authored so there is no third-party
  // licence attached to the app (Rule 56). Same slashed-pair convention as
  // mic / micOff above.
  eye: {
    mode: 'stroke',
    body: (
      <g>
        <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z" />
        <circle cx="12" cy="12" r="3" />
      </g>
    ),
  },
  eyeOff: {
    mode: 'stroke',
    body: (
      <g>
        <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z" />
        <circle cx="12" cy="12" r="3" />
        <line x1="3" y1="3" x2="21" y2="21" />
      </g>
    ),
  },
};

export default function UiIcon({ name, className = 'h-4 w-4', style }) {
  const def = UI_ICON_DEFS[name];
  if (!def) return null;

  if (def.mode === 'stroke') {
    return (
      <svg
        viewBox="0 0 24 24" className={className} style={style}
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true" focusable="false"
      >
        {def.body}
      </svg>
    );
  }
  // 'fill' or 'mixed' — let the body declare its own stroke/fill where needed.
  return (
    <svg
      viewBox="0 0 24 24" className={className} style={style}
      aria-hidden="true" focusable="false"
    >
      {def.body}
    </svg>
  );
}

export { UI_ICON_DEFS };
